import type { Memo } from 'models/Memo';
import type { MemoPayload } from 'networking/mindfeed/models/Memo';
import { QueryKey } from 'core/react-query/queryClient';
import { boxesQuery } from 'networking/mindfeed/queries/boxes';
import { notify, NotificationType } from 'xstate/actors/NotificationActor';
import { useActiveSpace } from 'xstate/hooks/useActiveSpace';
import { saveMemo } from 'networking/mindfeed/services/memo.service';
import { useOptimisticMemoMutation } from './useOptimisticMemoMutation';

export type Variables = {
  memo: MemoPayload;
};

export const useSaveMemo = ({
  onSuccess,
  shouldNotify = true,
}: {
  onSuccess?: (data: Memo) => void;
  shouldNotify?: boolean;
} = {}) => {
  const { selectedSpace, selectedBox } = useActiveSpace();

  return useOptimisticMemoMutation<Memo, Variables>({
    mutationFn: ({ memo }) => saveMemo(memo, selectedBox),
    // A new memo has no id in the cache yet, so we only optimistically update
    // edits to an existing one; new memos are inserted from the server result.
    // A cleared date is the server's to resolve, so it isn't applied here.
    optimistic: (cache, { memo }) => {
      if (cache.contains(memo.id)) {
        const { datedAt, ...rest } = memo;
        cache.update(memo.id, datedAt ? { ...rest, datedAt } : rest);
      }
    },
    onSuccess: (memo, _variables, { cache, queryClient }) => {
      // The memo is in the cache iff this was an edit (we never optimistically
      // inserted a new one), which also tells us which notification to show.
      const existed = cache.contains(memo.id);

      if (existed) cache.replace(memo);
      else cache.prepend(memo);

      // A new memo changes its box's memo count in the sidebar and the
      // Journal's day counts. An edit may have moved the memo to another day.
      if (!existed) {
        queryClient.invalidateQueries({
          queryKey: boxesQuery(selectedSpace?.id).queryKey,
        });
      }
      queryClient.invalidateQueries({
        queryKey: [QueryKey.BOX_ACTIVITY, memo.boxId],
      });
      // Its photos, videos, audio and files appear in the Library and Files.
      queryClient.invalidateQueries({
        queryKey: [QueryKey.BOX_MEDIA, memo.boxId],
      });

      if (shouldNotify) {
        notify(
          existed
            ? NotificationType.MEMO_UPDATED
            : NotificationType.MEMO_CREATED,
        );
      }

      onSuccess?.(memo);
    },
  });
};
